import React, { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { usePageViewTracker } from "@/hooks/useAnalytics";

// En desarrollo usa localhost:8000, en producción usa URL relativa
const BACKEND_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';

// Layout - Lazy loading para reducir bundle inicial
const Layout = lazy(() => import("@/components/Layout"));
const RelLayout = lazy(() => import("@/components/RelLayout"));

// Auth Components
import { AuthProvider, ProtectedRoute } from "@/components/AdminAuth";
import { JugadorAuthProvider, useJugadorAuth } from "@/hooks/useJugadorAuth";
import MigradoRedirect from "@/components/MigradoRedirect";

// Lazy loading para páginas - se cargan solo cuando se necesitan
const Jugadores = lazy(() => import("@/pages/Jugadores"));
const JugadorDetalle = lazy(() => import("@/pages/JugadorDetalle"));
const NuevoJugador = lazy(() => import("@/pages/NuevoJugador"));
// Empresarial Pages - Lazy
const HomeBoliche = lazy(() => import("@/pages/HomeBoliche"));
const ApuestasRanking = lazy(() => import("@/pages/apuestas/ApuestasRanking"));
const Historico = lazy(() => import("@/pages/historico/Historico"));
const HistoricoJugador = lazy(() => import("@/pages/historico/JugadorDetalle"));
const CrucesRivales = lazy(() => import("@/pages/historico/CrucesRivales"));
const HandicapLab = lazy(() => import("@/pages/handicap/HandicapLab"));
const TAenfrentamientos = lazy(() => import("@/pages/torneo_actual/TAenfrentamientos"));
const TAcaptura = lazy(() => import("@/pages/torneo_actual/TAcaptura"));
const TAplanilla = lazy(() => import("@/pages/torneo_actual/TAplanilla"));
const TAstats = lazy(() => import("@/pages/torneo_actual/TAstats"));
const TAjugadores = lazy(() => import("@/pages/torneo_actual/TAjugadores"));
const TAjuegos = lazy(() => import("@/pages/torneo_actual/TAjuegos"));
const TAhandicap = lazy(() => import("@/pages/torneo_actual/TAhandicap"));
const TApronostico = lazy(() => import("@/pages/torneo_actual/TApronostico"));
const TAsimuladorHdc = lazy(() => import("@/pages/torneo_actual/TAsimuladorHdc"));
const LigaLista = lazy(() => import("@/pages/liga/LigaLista"));
const LigaAdmin = lazy(() => import("@/pages/liga/LigaAdmin"));
const LigaConfig = lazy(() => import("@/pages/liga/LigaConfig"));
const LigaPublica = lazy(() => import("@/pages/liga/LigaPublica"));
const LigaMi = lazy(() => import("@/pages/liga/LigaMi"));
const LigaFotosTecnico = lazy(() => import("@/pages/liga/LigaFotosTecnico"));
const LigaTV = lazy(() => import("@/pages/liga/LigaTV"));
const IndLista = lazy(() => import("@/pages/individuales/IndLista"));
const IndAdmin = lazy(() => import("@/pages/individuales/IndAdmin"));
const IndLaboratorio = lazy(() => import("@/pages/individuales/IndLaboratorio"));
const IndPublica = lazy(() => import("@/pages/individuales/IndPublica"));
const EncLista = lazy(() => import("@/pages/encuestas/EncLista"));
const EncAdmin = lazy(() => import("@/pages/encuestas/EncAdmin"));
const EncResponder = lazy(() => import("@/pages/encuestas/EncResponder"));
const EncResultadosPublicos = lazy(() => import("@/pages/encuestas/EncResponder").then(m => ({ default: m.EncResultadosPublicos })));
const IndMi = lazy(() => import("@/pages/individuales/IndMi"));
const IndFotosTecnico = lazy(() => import("@/pages/individuales/IndFotosTecnico"));
const IndTV = lazy(() => import("@/pages/individuales/IndTV"));
const IndPresas = lazy(() => import("@/pages/individuales/IndPresas"));
const TAduelos = lazy(() => import("@/pages/torneo_actual/TAduelos"));
const TAequipos = lazy(() => import("@/pages/torneo_actual/TAequipos"));
const TAranking = lazy(() => import("@/pages/torneo_actual/TAranking"));
const TAcalendario = lazy(() => import("@/pages/torneo_actual/TAcalendario"));
const TAhistorial = lazy(() => import("@/pages/torneo_actual/TAhistorial"));
const TAvalidacion = lazy(() => import("@/pages/torneo_actual/TAvalidacion"));
const TAsandbagging = lazy(() => import("@/pages/torneo_actual/TAsandbagging"));
const TApreguntar = lazy(() => import("@/pages/torneo_actual/TApreguntar"));
const TAtextoCanal = lazy(() => import("@/pages/torneo_actual/TAtextoCanal"));
const TAvideo = lazy(() => import("@/pages/torneo_actual/TAvideo"));
const TAinforme = lazy(() => import("@/pages/torneo_actual/TAinforme"));
const TAinformeJugador = lazy(() => import("@/pages/torneo_actual/TAinformeJugador"));
const TAresumenJornada = lazy(() => import("@/pages/torneo_actual/TAresumenJornada"));
const TApronosticoVsReal = lazy(() => import("@/pages/torneo_actual/TApronosticoVsReal"));

// Relámpago Pages - Lazy
const RelTorneos = lazy(() => import("@/pages/relampago/RelTorneos"));
const TAfotosTecnico = lazy(() => import("@/pages/torneo_actual/TAfotosTecnico"));
const RelRankingTemporada = lazy(() => import("@/pages/relampago/RelRankingTemporada"));
const RelPalmares = lazy(() => import("@/pages/relampago/RelPalmares"));

// Mercadito Pages - Lazy
const MercadoLista = lazy(() => import("@/pages/mercado/MercadoLista"));
const MercadoDetalle = lazy(() => import("@/pages/mercado/MercadoDetalle"));
const MercadoPublicar = lazy(() => import("@/pages/mercado/MercadoPublicar"));
const MisProductos = lazy(() => import("@/pages/mercado/MisProductos"));
const MiPerfil = lazy(() => import("@/pages/jugador/MiPerfil"));
const AdminPerfiles = lazy(() => import("@/pages/admin/AdminPerfiles"));
const CuentasPendientes = lazy(() => import("@/pages/admin/CuentasPendientes"));
const NoticiasAdmin = lazy(() => import("@/pages/admin/NoticiasAdmin"));
const Visitas = lazy(() => import("@/pages/admin/Visitas"));
const UsoIA = lazy(() => import("@/pages/admin/UsoIA"));
const AjustesSitio = lazy(() => import("@/pages/admin/AjustesSitio"));

const CanalPublico = lazy(() => import("@/pages/publico/CanalPublico"));
const Aprende = lazy(() => import("@/pages/publico/Aprende"));
const MiTorneo = lazy(() => import("@/pages/jugador/MiTorneo"));

// Noticias Pages - Lazy
const NoticiasLista = lazy(() => import("@/pages/publico/Noticias").then(m => ({ default: m.NoticiasLista })));
const NoticiaDetalle = lazy(() => import("@/pages/publico/Noticias").then(m => ({ default: m.NoticiaDetalle })));

// Simulador Promedios (Admin) - Lazy
const SimuladorPromedios = lazy(() => import("@/pages/SimuladorPromedios"));

// Loading spinner para lazy components - Optimizado para Safari
const PageLoader = () => (
  <div className="min-h-screen bg-[#030305] flex items-center justify-center" style={{ willChange: 'auto' }}>
    <div className="text-center">
      <Loader2 className="h-12 w-12 text-cyan-500 animate-spin mx-auto mb-4" style={{ willChange: 'transform' }} />
      <p className="text-gray-400">Cargando...</p>
    </div>
  </div>
);

// Wrapper para páginas con Layout - Maneja lazy loading
const LayoutWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>
    <Layout>{children}</Layout>
  </Suspense>
);

// Wrapper PROTEGIDO para páginas que requieren PIN de admin
const ProtectedLayoutWrapper = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute>
    <Suspense fallback={<PageLoader />}>
      <Layout>{children}</Layout>
    </Suspense>
  </ProtectedRoute>
);

// Wrapper para RelLayout
const RelLayoutWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>
    <RelLayout>{children}</RelLayout>
  </Suspense>
);

// Links viejos a /mercado/auth?redirect=… → el ingreso con PIN de Mi Perfil
const MercadoAuthRedirect = () => {
  const location = useLocation();
  const destino = new URLSearchParams(location.search).get("redirect") || "/mercado";
  return <Navigate to={`/mi-perfil?next=${encodeURIComponent(destino.startsWith("/mercado") ? destino : "/mercado")}`} replace />;
};

// Wrapper protegido para portal del torneo (solo jugadores autenticados)
const JugadorPortalProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useJugadorAuth();
  const location = useLocation();

  if (loading) return <PageLoader />;

  if (!isAuthenticated) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/mi-perfil?next=${next}`} replace />;
  }

  return <>{children}</>;
};

// Auto-track all SPA page views + scroll depth + time on page
const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  usePageViewTracker();
  return <>{children}</>;
};

function AppUpdateBanner() {
  const [show, setShow] = useState(() => !!(window as any).__swUpdated);
  useEffect(() => {
    const handler = () => setShow(true);
    window.addEventListener('sw-updated', handler);
    return () => window.removeEventListener('sw-updated', handler);
  }, []);
  if (!show) return null;
  return (
    <div className="fixed bottom-20 left-0 right-0 z-50 flex justify-center px-4">
      <button
        onClick={() => window.location.reload()}
        className="flex items-center gap-2 bg-gray-900 text-white text-sm font-bold px-5 py-3 rounded-2xl shadow-xl border border-gray-700 active:scale-95 transition-transform"
      >
        <span>🔄</span>
        <span>Nueva versión disponible — Actualizar</span>
      </button>
    </div>
  );
}

// ── Pantalla de Mantenimiento ─────────────────────────────────────────────────
interface MantenimientoData { activo: boolean; titulo?: string; mensaje?: string }

function MantenimientoScreen({ titulo, mensaje }: { titulo: string; mensaje: string }) {
  return (
    <div className="min-h-screen bg-[#f4f4f5] flex items-center justify-center p-6"
      style={{ fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif" }}>
      <div className="text-center max-w-md">
        <h1 className="text-2xl sm:text-3xl font-semibold text-[#3f3f46] mb-3 leading-tight">
          {titulo}
        </h1>
        <p className="text-[#71717a] text-base leading-relaxed whitespace-pre-line">{mensaje}</p>
      </div>
    </div>
  );
}

function AppContenido() {
  const [mantenimiento, setMantenimiento] = React.useState<MantenimientoData | null>(null);
  const BACKEND_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';

  React.useEffect(() => {
    fetch(`${BACKEND_URL}/api/config/mantenimiento`)
      .then(r => r.json())
      .then(d => setMantenimiento(d))
      .catch(() => setMantenimiento({ activo: false }));
    // re-chequear cada 60 segundos
    const interval = setInterval(() => {
      fetch(`${BACKEND_URL}/api/config/mantenimiento`)
        .then(r => r.json())
        .then(d => setMantenimiento(d))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  if (mantenimiento?.activo) {
    return (
      <MantenimientoScreen
        titulo={mantenimiento.titulo ?? "Sitio en Mantenimiento"}
        mensaje={mantenimiento.mensaje ?? "Estamos realizando mejoras. Volvemos pronto. 🎳"}
      />
    );
  }

  return (
    <>
      <AppUpdateBanner />
      <BrowserRouter>
        <AnalyticsProvider>
        <AuthProvider>
          <JugadorAuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Suspense fallback={<PageLoader />}><HomeBoliche /></Suspense>} />
              {/* Secciones viejas que se quitaron (oct-2026): sus links caen en el inicio o en el panel del torneo actual */}
              <Route path="/dashboard" element={<Navigate to="/torneo-actual" replace />} />
              {["/torneo/*", "/torneos/*", "/quiniela", "/clinica", "/tripletas/*", "/ranking/*", "/encuesta/*", "/inscripcion/*",
                "/reuniones/*", "/partidas/*", "/equipos/*", "/posiciones", "/estadisticas", "/cruces", "/enfrentamientos",
                "/capitan", "/captura-ia", "/simulador-handicap", "/inicio-clasico"].map(p => (
                <Route key={p} path={p} element={<Navigate to="/" replace />} />
              ))}

              {/* ========== RUTAS PÚBLICAS (SIN PIN) ========== */}
              <Route path="/jugadores" element={<LayoutWrapper><Jugadores /></LayoutWrapper>} />
              <Route path="/jugadores/nuevo" element={<LayoutWrapper><NuevoJugador /></LayoutWrapper>} />
              <Route path="/jugadores/:jugadorId" element={<LayoutWrapper><JugadorDetalle /></LayoutWrapper>} />

              {/* ========== RUTAS PROTEGIDAS CON PIN DE ADMIN ========== */}
              {/* Torneo actual — captura rápida aislada (Supabase) */}
              <Route path="/torneo-actual" element={<ProtectedLayoutWrapper><TAenfrentamientos /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/stats" element={<ProtectedLayoutWrapper><TAstats /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/jugadores" element={<ProtectedLayoutWrapper><TAjugadores /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/juegos" element={<ProtectedLayoutWrapper><TAjuegos /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/handicap" element={<ProtectedLayoutWrapper><TAhandicap /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/pronostico" element={<ProtectedLayoutWrapper><TApronostico /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/simulador-hdc" element={<ProtectedLayoutWrapper><TAsimuladorHdc /></ProtectedLayoutWrapper>} />
              <Route path="/individuales" element={<ProtectedLayoutWrapper><IndLista /></ProtectedLayoutWrapper>} />
              <Route path="/individuales/laboratorio" element={<ProtectedLayoutWrapper><IndLaboratorio /></ProtectedLayoutWrapper>} />
              <Route path="/individuales/:id" element={<ProtectedLayoutWrapper><IndAdmin /></ProtectedLayoutWrapper>} />
              <Route path="/encuestas" element={<ProtectedLayoutWrapper><EncLista /></ProtectedLayoutWrapper>} />
              <Route path="/encuestas/:id" element={<ProtectedLayoutWrapper><EncAdmin /></ProtectedLayoutWrapper>} />
              {/* juegos individuales públicos: sin el menú lateral del admin */}
              <Route path="/individual/mi" element={<Suspense fallback={<PageLoader />}><IndMi /></Suspense>} />
              <Route path="/individual/:id/fotos" element={<Suspense fallback={<PageLoader />}><IndFotosTecnico /></Suspense>} />
              <Route path="/individual/:id/tv" element={<Suspense fallback={<PageLoader />}><IndTV /></Suspense>} />
              <Route path="/individual/:id/presas" element={<Suspense fallback={<PageLoader />}><IndPresas /></Suspense>} />
              <Route path="/individual/:id" element={<Suspense fallback={<PageLoader />}><IndPublica /></Suspense>} />
              <Route path="/encuesta/:token" element={<Suspense fallback={<PageLoader />}><EncResponder /></Suspense>} />
              <Route path="/encuesta/:token/resultados" element={<Suspense fallback={<PageLoader />}><EncResultadosPublicos /></Suspense>} />
              <Route path="/liga" element={<ProtectedLayoutWrapper><LigaLista /></ProtectedLayoutWrapper>} />
              {/* páginas del jugador: sin el menú lateral del admin */}
              <Route path="/liga/mi" element={<Suspense fallback={<PageLoader />}><LigaMi /></Suspense>} />
              <Route path="/liga/:id/fotos" element={<Suspense fallback={<PageLoader />}><LigaFotosTecnico /></Suspense>} />
              <Route path="/liga/:id/tv" element={<Suspense fallback={<PageLoader />}><LigaTV /></Suspense>} />
              <Route path="/liga/:id" element={<ProtectedLayoutWrapper><LigaAdmin /></ProtectedLayoutWrapper>} />
              <Route path="/liga/:id/config" element={<ProtectedLayoutWrapper><LigaConfig /></ProtectedLayoutWrapper>} />
              <Route path="/liga/:id/publico" element={<Suspense fallback={<PageLoader />}><LigaPublica /></Suspense>} />
              <Route path="/torneo-actual/duelos" element={<ProtectedLayoutWrapper><TAduelos /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/equipos" element={<ProtectedLayoutWrapper><TAequipos /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/ranking" element={<ProtectedLayoutWrapper><TAranking /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/calendario" element={<ProtectedLayoutWrapper><TAcalendario /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/historial" element={<ProtectedLayoutWrapper><TAhistorial /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/validacion" element={<ProtectedLayoutWrapper><TAvalidacion /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/sandbagging" element={<ProtectedLayoutWrapper><TAsandbagging /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/preguntar" element={<ProtectedLayoutWrapper><TApreguntar /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/texto-canal" element={<ProtectedLayoutWrapper><TAtextoCanal /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/video" element={<ProtectedLayoutWrapper><TAvideo /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/informe" element={<ProtectedLayoutWrapper><TAinforme /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/informe-jugador" element={<ProtectedLayoutWrapper><TAinformeJugador /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/resumen" element={<ProtectedLayoutWrapper><TAresumenJornada /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/pronostico-vs-real" element={<ProtectedLayoutWrapper><TApronosticoVsReal /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/enfrentamiento/:id/captura" element={<ProtectedLayoutWrapper><TAcaptura /></ProtectedLayoutWrapper>} />
              <Route path="/torneo-actual/enfrentamiento/:id" element={<ProtectedLayoutWrapper><TAplanilla /></ProtectedLayoutWrapper>} />
              <Route path="/admin/simulador-promedios" element={<ProtectedLayoutWrapper><SimuladorPromedios /></ProtectedLayoutWrapper>} />
              <Route path="/admin/perfiles" element={<ProtectedRoute><AdminPerfiles /></ProtectedRoute>} />
              <Route path="/admin/pendientes" element={<ProtectedRoute><CuentasPendientes /></ProtectedRoute>} />
              <Route path="/admin/noticias" element={<ProtectedLayoutWrapper><NoticiasAdmin /></ProtectedLayoutWrapper>} />
              <Route path="/admin/visitas" element={<ProtectedLayoutWrapper><Visitas /></ProtectedLayoutWrapper>} />
              <Route path="/admin/uso-ia" element={<ProtectedLayoutWrapper><UsoIA /></ProtectedLayoutWrapper>} />
              <Route path="/admin/ajustes" element={<ProtectedLayoutWrapper><AjustesSitio /></ProtectedLayoutWrapper>} />
              {/* Herraduras viejas (AP_*, etapa 8b): solo queda su ranking; una noche vieja lleva a la migrada */}
              <Route path="/apuestas" element={<Navigate to="/apuestas/ranking" replace />} />
              <Route path="/apuestas/confirmar/:jugadorApuestaId" element={<Navigate to="/apuestas/ranking" replace />} />
              <Route path="/apuestas/ranking" element={<Suspense fallback={<PageLoader />}><ApuestasRanking /></Suspense>} />
              <Route path="/apuestas/:torneoId/*" element={<MigradoRedirect a={id => `/individual/${id}`}><Navigate to="/apuestas/ranking" replace /></MigradoRedirect>} />

              {/* Acceso para Capitanes - SIN protección de admin */}

              {/* ========== RANKING - SIN PROTECCIÓN (PÚBLICO) ========== */}

            {/* Página pública para compartir - Sin Layout */}

            {/* Tripletas Routes */}

            {/* Relámpago Routes - Layout separado */}
            <Route path="/relampago" element={<RelLayoutWrapper><RelTorneos /></RelLayoutWrapper>} />
            <Route path="/relampago/historico" element={<RelLayoutWrapper><Historico /></RelLayoutWrapper>} />
            <Route path="/relampago/historico/jugador/:nombre" element={<RelLayoutWrapper><HistoricoJugador /></RelLayoutWrapper>} />
            <Route path="/relampago/historico/cruces" element={<RelLayoutWrapper><CrucesRivales /></RelLayoutWrapper>} />
            <Route path="/relampago/handicap" element={<RelLayoutWrapper><HandicapLab /></RelLayoutWrapper>} />
            <Route path="/relampago/ranking" element={<RelLayoutWrapper><RelRankingTemporada /></RelLayoutWrapper>} />
            <Route path="/relampago/palmares" element={<RelLayoutWrapper><RelPalmares /></RelLayoutWrapper>} />
            {/* links del Relámpago viejo (borrado en la etapa 8b): llevan a la noche migrada en la base nueva; si no, a la lista */}
            <Route path="/relampago/confirmar/:inscripcionId" element={<Navigate to="/relampago" replace />} />
            <Route path="/relampago/:torneoId/admin" element={<MigradoRedirect a={id => `/individuales/${id}`}><Navigate to="/relampago" replace /></MigradoRedirect>} />
            <Route path="/relampago/:torneoId/captura" element={<MigradoRedirect a={id => `/individuales/${id}?tab=captura`}><Navigate to="/relampago" replace /></MigradoRedirect>} />
            <Route path="/relampago/:torneoId/se-busca/tv" element={<MigradoRedirect a={id => `/individual/${id}/tv`}><Navigate to="/relampago" replace /></MigradoRedirect>} />
            <Route path="/relampago/:torneoId/*" element={<MigradoRedirect a={id => `/individual/${id}`}><Navigate to="/relampago" replace /></MigradoRedirect>} />
            {/* técnico del club, torneo actual: link secreto (?t=), sin cuenta */}
            <Route path="/torneo-actual/fotos" element={<Suspense fallback={<PageLoader />}><TAfotosTecnico /></Suspense>} />

            {/* ========== MERCADITO ========== */}
            <Route path="/mercado" element={<Suspense fallback={<PageLoader />}><MercadoLista /></Suspense>} />
            <Route path="/mi-perfil" element={<Suspense fallback={<PageLoader />}><MiPerfil /></Suspense>} />
            {/* El Mercadito usa el mismo ingreso con PIN de Mi Perfil (el flujo viejo por SMS se retiró) */}
            <Route path="/mercado/auth" element={<MercadoAuthRedirect />} />
            <Route path="/mercado/publicar" element={<Suspense fallback={<PageLoader />}><MercadoPublicar /></Suspense>} />
            <Route path="/mercado/mis-productos" element={<Suspense fallback={<PageLoader />}><MisProductos /></Suspense>} />
            <Route path="/mercado/editar/:productoId" element={<Suspense fallback={<PageLoader />}><MercadoPublicar /></Suspense>} />
            <Route path="/mercado/:productoId" element={<Suspense fallback={<PageLoader />}><MercadoDetalle /></Suspense>} />
            
            {/* ========== PÚBLICO - TORNEO EMPRESARIAL ========== */}

            {/* ========== QUINIELA BOLICHERA ========== */}
            <Route path="/canal" element={<Suspense fallback={<PageLoader />}><CanalPublico /></Suspense>} />
            <Route path="/aprende" element={<Suspense fallback={<PageLoader />}><Aprende /></Suspense>} />
            <Route path="/mi-torneo" element={<JugadorPortalProtectedRoute><Suspense fallback={<PageLoader />}><MiTorneo /></Suspense></JugadorPortalProtectedRoute>} />

            {/* ========== CLÍNICA ILDEMARO ========== */}

            {/* ========== NOTICIAS ========== */}
            <Route path="/noticias" element={<Suspense fallback={<PageLoader />}><NoticiasLista /></Suspense>} />
            <Route path="/noticias/:id" element={<Suspense fallback={<PageLoader />}><NoticiaDetalle /></Suspense>} />

            {/* Simuladores - Protegidos con PIN de admin */}
            </Routes>
          </Suspense>
          </JugadorAuthProvider>
        </AuthProvider>
        </AnalyticsProvider>
      </BrowserRouter>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AppContenido />
    </div>
  );
}

export default App;
